/* Classic SLVR bundles. The existing Shopify discounts determine the charged price. */
(function () {
  function groupItems(ids) {
    const quantities = new Map();
    ids.forEach((id) => quantities.set(String(id), (quantities.get(String(id)) || 0) + 1));
    return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
  }

  function quote(variants, discounts, rate) {
    if (!variants.length || variants.some((variant) => !variant)) throw new Error('Choose a colour for every pillowcase.');
    const discount = Number(discounts[variants.length]);
    if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(discount) || discount < 0) {
      throw new Error('Bundle pricing is unavailable. Please refresh the page.');
    }
    const regular = variants.reduce((sum, variant) => sum + variant.price, 0);
    const saving = Math.min(regular, Math.round(discount * rate * 100));
    return { regular, saving, total: regular - saving, each: (regular - saving) / variants.length };
  }

  function selectionError(ids, variants) {
    if (!Number.isInteger(ids.length) || ids.length < 1 || ids.length > 10) return 'Choose between 1 and 10 pillowcases.';
    for (const item of groupItems(ids)) {
      const variant = variants.find((entry) => String(entry.id) === item.id);
      if (!variant || !variant.available) return 'Choose an available colour for every pillowcase.';
      if (variant.managed && variant.policy !== 'continue' && item.quantity > variant.inventory) {
        return `Only ${Math.max(0, variant.inventory)} available in ${variant.title}. Please choose another colour or a smaller bundle.`;
      }
      if (item.quantity < variant.minimum || item.quantity % variant.increment !== 0 ||
          (variant.maximum != null && item.quantity > variant.maximum)) {
        return `The quantity selected for ${variant.title} does not meet its quantity rules.`;
      }
    }
    return '';
  }

  function writeItems(formData, items) {
    // FormData is used by both the custom drawer and Dawn. Keep form controls intact
    // for variant/gallery updates; change only the outgoing request, once per submit.
    const properties = Array.from(formData.entries()).filter(([key]) => /^properties\[.+\]$/.test(key));
    const sellingPlan = formData.get('selling_plan');
    Array.from(formData.keys()).forEach((key) => {
      if (key === 'id' || key === 'quantity' || key === 'selling_plan' || /^properties\[|^items\[/.test(key)) formData.delete(key);
    });
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, String(item.quantity));
      properties.forEach(([key, value]) => formData.append(`items[${index}][properties]${key.slice(10)}`, value));
      if (sellingPlan) formData.append(`items[${index}][selling_plan]`, sellingPlan);
    });
  }

  // Pure helpers are also exercised by the local Node regression tests.
  if (typeof module !== 'undefined' && module.exports) module.exports = { groupItems, quote, selectionError, writeItems };
  if (typeof customElements === 'undefined' || customElements.get('slvr-bundle-picker')) return;

  class SlvrBundlePicker extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = document.getElementById(this.dataset.formId);
      this.productInfo = this.closest('product-info');
      if (!this.form || !this.productInfo) return;
      if (!this.data) {
        this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
        this.mainId = String(this.form.querySelector('[name="id"]')?.value || this.data.selectedVariantId);
        this.quantity = Math.min(4, Math.max(1, Number(this.dataset.defaultQuantity) || 2));
        this.extraQuantity = 4;
        this.choices = Array(10).fill(this.mainId);
        this.customised = Array(10).fill(false);
      }
      this.form.slvrBundle = this;
      this.querySelectorAll('.slvr-bundle__choice input').forEach((input) => {
        input.addEventListener('change', () => {
          this.quantity = Number(input.value) === 4 ? this.extraQuantity : Number(input.value);
          this.update();
        }, { signal });
      });
      // Classic uses a <select> for 4+; Satin uses −/+ buttons.
      this.querySelector('[data-bundle-quantity]')?.addEventListener('change', (event) => {
        this.extraQuantity = Number(event.target.value);
        this.quantity = this.extraQuantity;
        this.update();
      }, { signal });
      this.querySelectorAll('[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          this.extraQuantity = Math.min(this.maxQuantity, Math.max(4, this.extraQuantity + Number(button.dataset.qtyStep)));
          this.quantity = this.extraQuantity;
          this.update();
        }, { signal });
      });
      this.form.addEventListener('formdata', (event) => {
        writeItems(event.formData, groupItems(this.getVariantIds()));
      }, { signal });
      this.retryButton = this.querySelector('[data-variant-retry]');
      if (!this.retryButton) {
        this.retryButton = document.createElement('button');
        this.retryButton.type = 'button';
        this.retryButton.className = 'button button--secondary';
        this.retryButton.dataset.variantRetry = '';
        this.retryButton.textContent = 'Retry colour update';
        this.retryButton.hidden = true;
        this.querySelector('.slvr-bundle__error').after(this.retryButton);
      }
      this.retryButton.addEventListener('click', () => {
        const retry = this.variantRetry;
        if (!retry) return;
        this.variantRetry = null;
        this.retryButton.hidden = true;
        this.updateButton();
        retry();
      }, { signal });
      this.productInfo.addEventListener('slvr:variant-error', (event) => {
        this.pendingVariant = true;
        this.variantRetry = event.detail.retry;
        this.retryButton.hidden = false;
        this.updateButton();
      }, { signal });
      this.productInfo.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects, variant-radios')) {
          this.pendingVariant = true;
          this.variantRetry = null;
          this.retryButton.hidden = true;
          this.updateButton();
        }
      }, { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId) !== this.dataset.sectionId) return;
          const variant = event.data.variant;
          if (!variant) return;
          this.mainId = String(variant.id);
          const cached = this.data.variants.find((entry) => String(entry.id) === this.mainId);
          if (cached) Object.assign(cached, { price: variant.price, available: variant.available });
          this.choices = this.choices.map((id, index) => this.customised[index] ? id : this.mainId);
          this.pendingVariant = false;
          this.variantRetry = null;
          this.retryButton.hidden = true;
          // Run after the native button/stock subscribers have finished their updates.
          queueMicrotask(() => { if (this.isConnected) this.update(); });
        });
      }
      this.hidden = false;
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      if (this.form?.slvrBundle === this) delete this.form.slvrBundle;
    }

    getVariantIds(quantity = this.quantity) {
      // Single purchases always follow the main picker, without overwriting the
      // independent colours the customer has saved for a multi-item bundle.
      return quantity === 1 ? [this.mainId] : this.choices.slice(0, quantity);
    }
    variant(id) { return this.data.variants.find((entry) => String(entry.id) === String(id)); }
    isPreorder(variant) { return variant && variant.managed && variant.inventory <= 0 && variant.policy === 'continue'; }
    getQuote(quantity = this.quantity) {
      const rate = this.data.currency === this.data.storeCurrency ? 1 : Number(window.Shopify?.currency?.rate);
      return quote(this.getVariantIds(quantity).map((id) => this.variant(id)), this.data.discounts, rate);
    }
    money(cents) {
      const amount = Math.round(cents) / 100;
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: this.data.currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : undefined
      }).format(amount);
    }
    showError(message) {
      const error = this.querySelector('.slvr-bundle__error');
      error.textContent = message;
      error.hidden = !message;
    }
    validate() {
      let error = selectionError(this.getVariantIds(), this.data.variants);
      try { this.getQuote(); } catch (exception) { error = exception.message; }
      if (this.pendingVariant) error = this.variantRetry
        ? 'Your colour could not be updated. Retry or choose another colour above.'
        : 'Please wait for the colour to finish updating.';
      this.showError(error);
      return !error;
    }

    selectMainVariant(id) {
      const variant = this.variant(id);
      const picker = this.productInfo.querySelector('variant-selects');
      const groups = Array.from(picker?.querySelectorAll('fieldset, select') || []);
      const controls = groups.map((group, index) => Array.from(group.querySelectorAll('input[type="radio"], option'))
        .find((control) => control.value === variant?.options?.[index]));
      if (!variant || !variant.available || !groups.length || groups.length !== variant.options?.length || controls.some((control) => !control)) {
        this.update();
        this.showError('Please choose your colour using the swatches above.');
        return;
      }
      controls.forEach((control, index) => {
        if (control.tagName === 'OPTION') {
          groups[index].value = control.value;
          Array.from(groups[index].options).forEach((option) => option.toggleAttribute('selected', option === control));
        } else {
          control.checked = true;
        }
      });
      const last = controls[controls.length - 1];
      const target = last.tagName === 'OPTION' ? last.parentElement : last;
      // Use Dawn's normal request so gallery, URL, price and stock stay in sync.
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    renderRows(card, quantity) {
      if (this.isSatin) {
        this.renderSatinColours(card, quantity);
        return;
      }
      const rows = card.querySelector('[data-colour-rows]');
      const selectedIds = this.getVariantIds(quantity);
      card.querySelector('.slvr-bundle__help').textContent = quantity === 1 ? 'Choose your colour' : 'Mix & match your colours';
      rows.classList.toggle('slvr-bundle__colours--single', quantity === 1);
      if (rows.children.length !== quantity) {
        rows.replaceChildren();
        for (let index = 0; index < quantity; index++) {
          const row = document.createElement('div');
          row.className = 'slvr-bundle__row';
          const label = document.createElement('label');
          const select = document.createElement('select');
          select.id = `BundleColour-${this.dataset.sectionId}-${card.dataset.tier}-${index}`;
          label.htmlFor = select.id;
          label.textContent = `Pillowcase ${index + 1}`;
          const swatch = document.createElement('span');
          swatch.className = 'slvr-bundle__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          this.data.variants.forEach((variant) => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title + (!variant.available ? ' — Sold out' : '');
            option.disabled = !variant.available;
            select.append(option);
          });
          select.addEventListener('change', () => {
            if (quantity === 1) {
              this.selectMainVariant(select.value);
              return;
            }
            this.choices[index] = select.value;
            this.customised[index] = true;
            this.update();
          });
          row.append(label, swatch, select);
          rows.append(row);
        }
      }
      Array.from(rows.children).forEach((row, index) => {
        const variant = this.variant(selectedIds[index]);
        const select = row.querySelector('select');
        select.value = selectedIds[index];
        select.disabled = !!this.busy;
        row.querySelector('.slvr-bundle__swatch').style.background = variant?.swatch || 'transparent';
      });
    }

    /* ---------- Satin layout: display only. Pricing, validation and cart logic are shared. ---------- */
    get isSatin() { return this.dataset.layout === 'satin'; }
    // The largest bundle with a configured discount (10 today).
    get maxQuantity() { return Math.max(...Object.keys(this.data.discounts).map(Number)); }

    // One pillowcase photo; a colour without a photo falls back to its swatch colour.
    pillow(variant) {
      const pillow = document.createElement('span');
      pillow.className = 'slvr-satin__pillow';
      if (variant?.swatch) pillow.style.setProperty('--swatch', variant.swatch);
      if (!variant?.photo) {
        pillow.classList.add('is-fallback');
        return pillow;
      }
      const image = document.createElement('img');
      image.src = variant.photo;
      image.alt = '';
      image.width = 240;
      image.height = 180;
      image.decoding = 'async';
      image.addEventListener('error', () => {
        image.remove();
        pillow.classList.add('is-fallback');
      }, { once: true });
      pillow.append(image);
      return pillow;
    }

    renderPile(card, count, selected) {
      const pile = card.querySelector('[data-pile]');
      // The selected tier shows the shopper's mix; the others show the main colour.
      const ids = (selected ? this.getVariantIds(count) : Array(count).fill(this.mainId)).slice(0, 4);
      const key = `${count}:${ids.join(',')}`;
      if (pile.dataset.key === key) return;
      pile.dataset.key = key;
      const items = ids.map((id, index) => {
        const pillow = this.pillow(this.variant(id));
        pillow.style.setProperty('--i', index);
        return pillow;
      });
      if (count > 4) {
        const tag = document.createElement('b');
        tag.textContent = `×${count}`;
        items.push(tag);
      }
      pile.replaceChildren(...items);
    }

    updateSatinCard(card, tier, count, selected) {
      if (tier === 4) {
        card.querySelector('[data-satin-name]').textContent = count > 4 ? `${count} pillowcases` : '4+ pillowcases';
        card.querySelector('[data-qty-output]').textContent = count;
        card.querySelectorAll('[data-qty-step]').forEach((button) => {
          const atLimit = Number(button.dataset.qtyStep) < 0 ? count <= 4 : count >= this.maxQuantity;
          button.toggleAttribute('data-locked', atLimit);
          button.disabled = !!this.busy || atLimit;
        });
      }
      this.renderPile(card, count, selected);
      const each = card.querySelector('[data-each]');
      try {
        const price = this.getQuote(count);
        // Same per-country thresholds as the cart drawer, after discounts, for this bundle alone.
        const freeShipping = price.total >= this.data.rewards.freeShipping;
        const gift = price.total >= this.data.rewards.gift;
        // Like classic: when the bundle earns the bag, "Save" and the struck total include its value.
        // The per-pillowcase price stays pillowcases only; the charged price is unchanged.
        const giftRate = this.data.currency === this.data.storeCurrency ? 1 : Number(window.Shopify?.currency?.rate);
        const giftValue = this.data.giftValueBase * giftRate;
        card.querySelector('[data-saving]').hidden = !price.saving;
        card.querySelector('[data-saving-amount]').textContent = this.money(price.saving + (gift ? giftValue : 0));
        // "Was / now" per pillowcase: the single price struck through, then the bundle price.
        const now = document.createElement('b');
        now.textContent = this.money(price.each);
        const parts = [now, document.createTextNode(' each')];
        if (price.saving) {
          const was = document.createElement('s');
          was.textContent = this.money(price.regular / count);
          parts.unshift(was);
        }
        each.replaceChildren(...parts);
        card.querySelector('[data-bundle-shipping]').hidden = !freeShipping;
        card.querySelector('[data-bundle-gift]').hidden = !gift;
        // Optional so a cached page without the bag markup still works.
        const bag = card.querySelector('[data-bundle-bag]');
        if (bag) {
          bag.hidden = !gift;
          bag.querySelector('[data-bag-value]').textContent = this.money(giftValue);
        }
        card.querySelector('[data-total]').textContent = this.money(price.total);
        const regular = card.querySelector('[data-regular]');
        regular.textContent = this.money(price.regular + (gift ? giftValue : 0));
        regular.hidden = !price.saving;
        card.querySelector('[data-full-price]').hidden = !!price.saving;
      } catch (_) {
        ['[data-saving]', '[data-bundle-shipping]', '[data-bundle-gift]', '[data-regular]', '[data-full-price]']
          .forEach((selector) => { card.querySelector(selector).hidden = true; });
        card.querySelector('[data-bundle-bag]')?.toggleAttribute('hidden', true);
        each.textContent = 'Pricing unavailable';
        card.querySelector('[data-total]').textContent = '';
      }
      if (selected) this.renderRows(card, count);
    }

    renderSatinColours(card, quantity) {
      const ids = this.getVariantIds(quantity);
      this.activeSlot = Math.min(this.activeSlot || 0, quantity - 1);
      const active = quantity === 1 ? 0 : this.activeSlot;
      const current = this.variant(ids[active]);
      card.querySelector('[data-colour-title]').textContent = quantity === 1 ? 'Choose your colour' : 'Mix & match colours';
      const name = document.createElement('b');
      name.textContent = current?.title || '';
      card.querySelector('[data-colour-label]').replaceChildren(...(quantity === 1 ? [name] : [`Pillowcase ${active + 1} · `, name]));

      // Pillowcase buttons: pick which one to recolour.
      const slots = card.querySelector('[data-slots]');
      slots.hidden = quantity === 1;
      if (slots.children.length !== quantity) {
        slots.replaceChildren(...Array.from({ length: quantity }, (_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'slvr-satin__slot';
          button.dataset.satinControl = '';
          button.addEventListener('click', () => {
            this.activeSlot = index;
            this.renderSatinColours(card, quantity);
          });
          return button;
        }));
      }
      Array.from(slots.children).forEach((button, index) => {
        const variant = this.variant(ids[index]);
        if (button.dataset.id !== String(ids[index])) {
          button.dataset.id = ids[index];
          const number = document.createElement('span');
          number.textContent = index + 1;
          button.replaceChildren(this.pillow(variant), number);
        }
        button.classList.toggle('is-active', index === active);
        button.setAttribute('aria-pressed', String(index === active));
        button.setAttribute('aria-label', `Pillowcase ${index + 1}: ${variant?.title || ''}`);
        button.disabled = !!this.busy;
      });

      // Colour swatches for the chosen pillowcase.
      const palette = card.querySelector('[data-palette]');
      if (!palette.children.length) {
        palette.replaceChildren(...this.data.variants.map((variant) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'slvr-satin__swatch';
          button.dataset.satinControl = '';
          button.dataset.id = variant.id;
          button.title = variant.title;
          if (variant.swatch) button.style.setProperty('--swatch', variant.swatch);
          button.addEventListener('click', () => this.chooseSatinColour(String(variant.id)));
          return button;
        }));
      }
      Array.from(palette.children).forEach((button) => {
        const variant = this.variant(button.dataset.id);
        const on = String(ids[active]) === button.dataset.id;
        button.classList.toggle('is-sold-out', !variant.available);
        button.toggleAttribute('data-locked', !variant.available);
        button.setAttribute('aria-pressed', String(on));
        button.setAttribute('aria-label', `${variant.title}${variant.available ? '' : ' (sold out)'}`);
        button.disabled = !!this.busy || !variant.available;
      });
    }

    chooseSatinColour(id) {
      // A single pillowcase follows the main colour picker, exactly like the classic layout.
      if (this.quantity === 1) {
        this.selectMainVariant(id);
        return;
      }
      const index = Math.min(this.activeSlot || 0, this.quantity - 1);
      this.choices[index] = id;
      this.customised[index] = true;
      this.update();
    }

    update() {
      this.querySelectorAll('[data-tier]').forEach((card) => {
        const tier = Number(card.dataset.tier);
        const count = tier === 4 ? this.extraQuantity : tier;
        const selected = tier === Math.min(4, this.quantity);
        card.classList.toggle('is-selected', selected);
        card.querySelector('input').checked = selected;
        card.querySelector('.slvr-bundle__details').hidden = !selected;
        if (this.isSatin) {
          this.updateSatinCard(card, tier, count, selected);
          return;
        }
        try {
          const price = this.getQuote(count);
          card.querySelector('[data-saving]').hidden = !price.saving;
          // Match the drawer's per-country gift threshold (presentment currency, after discounts).
          // This describes the bundle alone; other cart items/codes are resolved in cart.
          const hasGift = price.total >= this.data.rewards.gift;
          card.querySelector('[data-gift-heading]').hidden = !hasGift;
          card.querySelector('[data-bundle-gift]').hidden = !hasGift || !selected;
          card.querySelector('[data-total-value]').hidden = !hasGift;
          const giftRate = this.data.currency === this.data.storeCurrency ? 1 : Number(window.Shopify?.currency?.rate);
          // Display total value saved, including the bag; the charged-price quote is unchanged.
          card.querySelector('[data-saving-amount]').textContent = this.money(price.saving + (hasGift ? this.data.giftValueBase * giftRate : 0));
          card.querySelector('[data-gift-value]').textContent = `${this.money(this.data.giftValueBase * giftRate)} value`;
          // Match the drawer's per-country free-shipping threshold (after discounts).
          const freeShipping = price.total >= this.data.rewards.freeShipping;
          const eachLabel = card.querySelector('[data-each]');
          eachLabel.dataset.freeShipping = String(freeShipping);
          card.querySelector('[data-bundle-shipping]').hidden = !freeShipping;
          card.querySelector('[data-total-caption]').hidden = count === 1;
          if (count === 1) {
            eachLabel.textContent = `${this.money(price.each)} each`;
          } else {
            const amount = document.createElement('span');
            amount.className = 'slvr-bundle__unit-number';
            amount.textContent = this.money(price.each);
            const wording = document.createElement('span');
            wording.className = 'slvr-bundle__unit-wording';
            wording.textContent = 'per pillowcase';
            eachLabel.replaceChildren(amount, document.createTextNode(' '), wording);
          }
          card.querySelector('[data-total]').textContent = this.money(price.total);
          card.querySelector('[data-regular]').textContent = this.money(price.regular + (hasGift ? this.data.giftValueBase * giftRate : 0));
          card.querySelector('[data-regular]').hidden = !price.saving;
        } catch (_) {
          card.querySelector('[data-saving]').hidden = true;
          card.querySelector('[data-bundle-gift]').hidden = true;
          card.querySelector('[data-gift-heading]').hidden = true;
          card.querySelector('[data-total-value]').hidden = true;
          card.querySelector('[data-bundle-shipping]').hidden = true;
          card.querySelector('[data-total-caption]').hidden = true;
          card.querySelector('[data-each]').dataset.freeShipping = 'false';
          card.querySelector('[data-each]').textContent = 'Pricing unavailable';
          card.querySelector('[data-total]').textContent = '';
          card.querySelector('[data-regular]').hidden = true;
        }
        if (selected) this.renderRows(card, count);
      });
      this.querySelector('[data-gift-terms]').textContent = `Free laundry bag on orders ${this.money(this.data.rewards.gift)}+ after discounts.`;
      this.updateButton();
      this.dispatchEvent(new CustomEvent('slvr:bundle-change', { bubbles: true }));
    }

    updateButton() {
      const button = this.form.querySelector('[type="submit"]');
      if (!button) return;
      const valid = this.validate();
      button.disabled = !valid || !!this.busy || !!this.pendingVariant;
      button.setAttribute('aria-disabled', String(!!this.busy));
      const label = button.querySelector('.product-form__submit-label');
      if (label) label.textContent = valid ? (window.variantStrings?.addToCart || 'Add to cart') : 'Check your selection';
      try {
        const total = this.money(this.getQuote().total);
        const price = button.querySelector('.product-form__submit-price');
        if (price) {
          const value = document.createElement('span');
          value.className = 'product-form__submit-current';
          // Match buy-buttons.liquid: AUD reads "A$" in the button.
          value.textContent = this.data.currency === 'AUD' && !total.includes('A$') ? total.replace('$', 'A$') : total;
          price.replaceChildren(value);
        }
        this.querySelector('[data-bundle-announcement]').textContent = `${this.quantity} pillowcase${this.quantity === 1 ? '' : 's'}, ${total}`;
      } catch (_) { /* validate() supplies the visible error and disables submission. */ }
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('input, select').forEach((control) => { control.disabled = busy; });
      // Satin buttons: sold-out colours and quantity limits stay disabled after a submit.
      this.querySelectorAll('[data-satin-control]').forEach((control) => { control.disabled = busy || control.hasAttribute('data-locked'); });
      // Prevent a slow variant request from changing the visible selection mid-submit.
      const picker = this.productInfo.querySelector('variant-selects, variant-radios');
      if (busy) {
        this.lockedControls = Array.from(picker?.querySelectorAll('input, select') || []).filter((control) => !control.disabled);
        this.lockedControls.forEach((control) => { control.disabled = true; });
      } else {
        this.lockedControls?.forEach((control) => { control.disabled = false; });
      }
      const button = this.form.querySelector('[type="submit"]');
      button?.classList.toggle('loading', busy);
      button?.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
      this.updateButton();
    }
  }
  customElements.define('slvr-bundle-picker', SlvrBundlePicker);
})();
